import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getMyGames, checkGameAccess } from '../api';
import Auth from './Auth';
import PaymentModal from './PaymentModal';
import './MyGames.css';

interface Game {
  id: number;
  player_name: string;
  current_cell: number;
  finished: boolean;
  created_at: string;
  moves_count: number;
  status: 'active' | 'finished';
  game_type?: 'paid' | 'free';
}

interface GamesResponse {
  games: Game[];
  pagination: {
    page: number;
    limit: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

const MyGames: React.FC = () => {
  const { user } = useAuth();
  const [games, setGames] = useState<Game[]>([]);
  const [pagination, setPagination] = useState<GamesResponse['pagination'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [creatingGame, setCreatingGame] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [playerName, setPlayerName] = useState('');
  const [hasGameAccess, setHasGameAccess] = useState(true);
  const [accessInfo, setAccessInfo] = useState<any>(null);

  const loadGames = async (page: number = 1) => {
    try {
      setLoading(true);

      // Проверяем доступ параллельно с играми
      const [gamesResponse, accessResponse] = await Promise.all([
        getMyGames(page, 20),
        checkGameAccess().catch(err => {
          console.warn('Failed to check game access:', err);
          return { has_access: true }; // fallback
        })
      ]);

      setError(null);
      setGames(gamesResponse.games);
      setPagination(gamesResponse.pagination);
      setHasGameAccess(accessResponse.has_access);
      setAccessInfo(accessResponse);
      setCurrentPage(page);
    } catch (err: any) {
      // Если это ошибка "User not found" - это нормально для новых пользователей
      if (err.message && err.message.includes('User not found')) {
        setError(null); // Не показываем ошибку для новых пользователей
        setGames([]); // Пустой список игр
        setPagination({
          page: 1,
          limit: 20,
          total_count: 0,
          total_pages: 0,
          has_next: false,
          has_prev: false
        });
      } else {
        setError('Ошибка загрузки игр');
        console.error('Error loading games:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadGames();
    }
  }, [user]);

  const handleCreateGameClick = () => {
    // Получаем имя пользователя из профиля для предзаполнения
    const profileName = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || '';
    setPlayerName(profileName);
    setShowCreateModal(true);
  };

  const handleCreateGame = async () => {
    if (!playerName.trim()) {
      setError('Введите имя игрока');
      return;
    }

    // Проверяем баланс ДО создания игры
    if (!hasGameAccess) {
      setShowCreateModal(false);
      setShowPaymentModal(true);
      return;
    }

    // Закрываем модальное окно
    setShowCreateModal(false);

    // Редиректим на страницу магии
    const encodedPlayerName = encodeURIComponent(playerName.trim());
    window.location.href = `/magic?playerName=${encodedPlayerName}&redirectTo=${encodeURIComponent('/my-games')}`;
  };


  const handleUseProfileName = () => {
    const profileName = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || '';
    setPlayerName(profileName);
  };

  const handleContinueGame = (gameId: number) => {
    window.location.href = `/?gameId=${gameId}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (game: Game) => {
    if (game.finished) return 'Завершена';
    if (game.current_cell === 68) return 'Формирование запроса';
    return 'Активна';
  };

  const getStatusClass = (game: Game) => {
    if (game.finished) return 'status-finished';
    if (game.current_cell === 68) return 'status-forming-request';
    return 'status-active';
  };

  if (!user) {
    console.log('User not authenticated, showing auth required page');
    return (
      <div className="my-games-container">
        <div className="auth-required">
          <div className="auth-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 12C14.7614 12 17 9.76142 17 7C17 4.23858 14.7614 2 12 2C9.23858 2 7 4.23858 7 7C7 9.76142 9.23858 12 12 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M20.59 22C20.59 18.13 16.74 15 12 15C7.26 15 3.41 18.13 3.41 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2>Войдите в аккаунт</h2>
          <p>Для просмотра ваших игр необходимо войти в аккаунт</p>
          <div className="auth-actions">
            <button 
              className="login-btn"
              onClick={() => {
                console.log('Login button clicked, setting showAuthModal to true');
                setShowAuthModal(true);
              }}
            >
              Войти в аккаунт
            </button>
            <a 
              href="/" 
              className="home-link"
            >
              ← Вернуться на главную
            </a>
          </div>
        </div>
        
        {/* Модальное окно авторизации */}
        {showAuthModal && (
          <div className="modal-overlay" onClick={() => {
            console.log('Modal overlay clicked, closing modal');
            setShowAuthModal(false);
          }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <Auth onClose={() => {
                console.log('Auth onClose called, closing modal');
                setShowAuthModal(false);
              }} />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="my-games-container">
      <div className="my-games-header">
        <h1>Мои игры</h1>
        {hasGameAccess ? (
          <button
            className="create-game-btn"
            onClick={handleCreateGameClick}
            disabled={creatingGame}
          >
            {creatingGame ? 'Создание...' : 'Новая игра'}
          </button>
        ) : (
          <button
            className="upgrade-btn payment-required"
            onClick={() => setShowPaymentModal(true)}
          >
            🔒 Оплатить доступ к играм
          </button>
        )}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {loading ? (
        <div className="loading">
          Загрузка игр...
        </div>
      ) : (
        <>
          {games.length === 0 ? (
            <div className="no-games">
              <h3>У вас пока нет игр</h3>
              <p>Создайте новую игру, чтобы начать играть!</p>
              {hasGameAccess ? (
                <button
                  className="create-game-btn-large"
                  onClick={handleCreateGameClick}
                  disabled={creatingGame}
                >
                  {creatingGame ? 'Создание...' : '🎮 Новая игра'}
                </button>
              ) : (
                <button
                  className="upgrade-btn-large payment-required"
                  onClick={() => setShowPaymentModal(true)}
                >
                  🔒 Оплатить доступ к играм
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="games-stats">
                <p>Всего игр: {pagination?.total_count || 0}</p>
              </div>

              <div className="games-list">
                {games.map((game) => (
                  <div key={game.id} className="game-card">
                    <div className="game-info">
                      <h3>
                        {game.player_name}
                        {game.game_type === 'paid' && (
                          <span className="paid-game-badge" title="Эта игра использует ваш платный баланс">
                            💎 Платная
                          </span>
                        )}
                      </h3>
                      <p className="game-date">{formatDate(game.created_at)}</p>
                      <p className={`game-status ${getStatusClass(game)}`}>
                        {getStatusText(game)}
                      </p>
                      <p className="game-position">
                        Позиция: {game.current_cell}/72
                      </p>
                    </div>
                    <div className="game-actions">
                      {game.finished ? (
                        <button 
                          className="view-btn"
                          onClick={() => handleContinueGame(game.id)}
                        >
                          Просмотреть
                        </button>
                      ) : (
                        <button 
                          className="continue-btn"
                          onClick={() => handleContinueGame(game.id)}
                        >
                          Продолжить
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {pagination && pagination.total_pages > 1 && (
                <div className="pagination">
                  <button 
                    onClick={() => loadGames(currentPage - 1)}
                    disabled={!pagination.has_prev}
                    className="pagination-btn"
                  >
                    ← Предыдущая
                  </button>
                  
                  <span className="pagination-info">
                    Страница {pagination.page} из {pagination.total_pages}
                  </span>
                  
                  <button 
                    onClick={() => loadGames(currentPage + 1)}
                    disabled={!pagination.has_next}
                    className="pagination-btn"
                  >
                    Следующая →
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Модальное окно создания игры */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Создать новую игру</h3>
            <p>Введите имя игрока:</p>
            
            <div className="name-input-section">
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Введите имя игрока"
                className="name-input"
                maxLength={50}
              />
              
              {(user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email) && (
                <button 
                  type="button"
                  onClick={handleUseProfileName}
                  className="use-profile-btn"
                >
                  Использовать имя из профиля
                </button>
              )}
            </div>

            <div className="modal-actions">
              <button 
                onClick={() => setShowCreateModal(false)}
                className="cancel-btn"
                disabled={creatingGame}
              >
                Отмена
              </button>
              <button 
                onClick={handleCreateGame}
                className="create-btn"
                disabled={creatingGame || !playerName.trim()}
              >
                {creatingGame ? 'Создание...' : 'Создать игру'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => setShowPaymentModal(false)}
          onPaymentSuccess={() => {
            setShowPaymentModal(false);
            loadGames(); // Refresh access status
          }}
        />
      )}
    </div>
  );
};

export default MyGames;
